import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../api';
import { NafCode } from './NafCodeSelector';

interface SelectedNafCodesProps {
  selectedCodes: string[];
  onRemove: (code: string) => void;
  className?: string;
}

const SelectedNafCodes: React.FC<SelectedNafCodesProps> = ({
  selectedCodes,
  onRemove,
  className = '',
}) => {
  const [nafCodesInfo, setNafCodesInfo] = useState<NafCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch NAF codes information when selected codes change
  useEffect(() => {
    const fetchNafCodesInfo = async () => {
      if (selectedCodes.length === 0) return;
      
      setIsLoading(true);
      try {
        // Fetch each code individually since we don't have a bulk endpoint
        const nafInfoPromises = selectedCodes.map(code => 
          api.get(`/naf/${code}`)
            .then(response => response.data)
            .catch(() => ({ code, intitule: 'Information non disponible' }))
        );
        
        const results = await Promise.all(nafInfoPromises);
        setNafCodesInfo(results);
      } catch (error) {
        console.error('Error fetching NAF codes information:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNafCodesInfo();
  }, [selectedCodes]);

  if (selectedCodes.length === 0) {
    return <p className="text-sm text-gray-500">Aucun code NAF spécifié</p>;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {isLoading ? (
        <span className="text-sm text-gray-500">Chargement des informations...</span>
      ) : (
        selectedCodes.map((code) => {
          const nafInfo = nafCodesInfo.find(info => info.code === code);
          return (
            <div 
              key={code} 
              className="inline-flex items-center rounded-full bg-blue-100 px-3 py-0.5 text-sm font-medium text-blue-800"
            >
              <span>{nafInfo ? `${nafInfo.code} - ${nafInfo.intitule}` : code}</span>
              <button
                type="button"
                onClick={() => onRemove(code)}
                className="ml-1.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:bg-blue-500 focus:text-white focus:outline-none"
              >
                <span className="sr-only">Supprimer {code}</span>
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })
      )}
    </div>
  );
};

export default SelectedNafCodes; 